import classNames from "classnames";
import LocalNotification from "lib/Notification";
import _ from "lodash";
import qs from "query-string";
import React, { memo, useCallback, useEffect, useState } from "react";
import { RouteComponentProps, withRouter, useHistory } from "react-router-dom";
import { Board, Button } from "v2/components/shared";
import { Form, Item as FormItem } from "v2/components/shared/Form";
import { useAddClassNames } from "v2/hooks";
import { getValidationStatus, isEmail } from "v2/utils";
import { setSmidgeUser } from "v2/utils/session";
import axios from "axios";

interface ISignInProps {
	fromUrl?: string;
}

const GoogleOAuthURI = () =>
	`${process.env.REACT_APP_API_ENDPOINT_V1}auth/google_oauth2?redirect_uri=${process.env.REACT_APP_API_ENDPOINT_V1}omniauth`

// WHAT GOES WRONG ON THE SSO PATH, in practice, is one thing: the email's
// domain has no OIDC configuration, so v1 sso/microsoft/authorization_url has
// nowhere to send the person and the request fails (Sim, 7 Sep 2026). The
// domain is the only part of what they typed they can do anything about, and
// "Can't process your request" named none of it.
//
// DELIBERATELY NOT USED ON THE PASSWORD PATH below, where a non-401 failure
// really is a request that could not be processed and has nothing to do with
// the domain.
const SSO_DOMAIN_ERROR = "Please check the email domain.";

const SignIn = (props: RouteComponentProps & ISignInProps) => {
	useAddClassNames("input", ["font-mont"]);

	const { location } = props;
	const history = useHistory();
	const [shouldReload, setShouldReload] = useState(false);
	const { email: predefinedEmail } = qs.parse(location.search);
	const [data, setData] = useState({ email: predefinedEmail as string, password: "" });
	const [error, setError] = useState<{ [key: string]: string }>({});
	const [loading, setLoading] = useState(false);
	const [isSsoAuth, setIsSsoAuth] = useState(false);
	const validated = getValidationStatus(data, error, isSsoAuth ? ["email"] : ["email", "password"]);
	const updateSmidgeUser = useCallback((cogUser: any) => {
		setSmidgeUser({
			id: cogUser.id,
			email: cogUser.email,
			name: cogUser.name,
			attributes: { phone_number: cogUser.phone_number }
		});
	}, []);

	const googleSignIn = () => {
		setLoading(true);
		window.open(GoogleOAuthURI(), "_self")
	}

	const ssoSignIn = () => {
		setIsSsoAuth(!isSsoAuth);
	}

	const microsoftSignIn = () => {
		setLoading(true);
		if(isSsoAuth){
			axios.get(`${process.env.REACT_APP_API_ENDPOINT_V1}sso/microsoft/authorization_url?email=${data.email}`).then((res) => {
				const authorizationUrl = _.get(res, 'data.authorization_url');
				window.location.href = authorizationUrl;
			})
			.catch((e) => {
				console.error('Error:', e);
				setLoading(false);
				setError({ ...error, email: SSO_DOMAIN_ERROR });
			});
		}else{
			setLoading(false);
			setError({ ...error, email: SSO_DOMAIN_ERROR });
		}
	}

	const onSignIn = async () => {
		setLoading(true);
		if(isSsoAuth){
			microsoftSignIn();
			return;
		}

		axios.post(`${process.env.REACT_APP_API_ENDPOINT_V2}sign_in`, {
				user: {
					email: data.email, 
					password: data.password
				},
			})
			.then((res) => {
				const access = _.get(res, 'data.data.tokens.access_token');
				const refresh = _.get(res, 'data.data.tokens.refresh_token');
				const user = _.get(res, 'data.data.user');
				// if (!(res.status >= 200 && res.status < 300)) {
				// 	setLoading(false);
				// 	setError({ ...error, email: "We don't recognise your email or password!" });
				// }			
				// createCookie('OAUTH', res.data.data.token, 60);
				if (access) localStorage.setItem('access_token', access);
				if (refresh) localStorage.setItem('refresh_token', refresh);
				if (user?.id) localStorage.setItem('userId', user.id);
				if (user) updateSmidgeUser(user);
				// Use a full-page navigation rather than history.push so that:
				//  - ProtectedRoute re-mounts with fresh localStorage tokens
				//  - Any stale `location.state.reload` from the prior redirect to
				//    /signin can't trigger the reload branch in the useEffect below
				//    and bounce the user back to /signin
				setLoading(false);
				window.location.href = props.fromUrl || "/";
			})
			.catch((e) => {
				console.log('Error:', e);
				setLoading(false);
				if (e?.response?.status === 401) {
					// The credentials were right but this account may not use a
					// password — the API says so with `code`, and that one case gets
					// the same error TOAST the blocked-SSO redirect produces, rather
					// than the inline field error, which would read as "wrong
					// password" and send the person round the same loop again.
					// Matched on the code, not the sentence, so the wording can change.
					if (e?.response?.data?.code === "password_login_disabled") {
						const detail = e?.response?.data?.errors;
						new LocalNotification(
							typeof detail === "string" && detail ? detail : "Password login is disabled for this account",
							"",
							"error"
						);
					} else {
						setError({ ...error, email: "We don't recognise your email or password!" });
					}
				} else {
					console.error('Error:', e);
					setError({ ...error, email: "Can't process your request" });
				}
			});
	};

	const onResetPWD = () => {
		history.push("/password/reset");
	};

	useEffect(() => {
		// Used for Google Authentication
		const searchParams = new URLSearchParams(window.location.search);
		const auth = searchParams.get('auth');
		const userId = searchParams.get('resCode');
		const access_token = searchParams.get('access_token') || "";
		const refresh_token = searchParams.get('refresh_token') || "";
		const email = searchParams.get('email');
		const msg = searchParams.get('msg');

		// User authenticated
		if (auth === "true" && userId) {
			localStorage.setItem('access_token', access_token)
			localStorage.setItem('refresh_token', refresh_token)
			localStorage.setItem('userId', userId);
			history.push('/')
		}
		else if (auth === "false") {
			var error_msg = email === null ? `Email does not exist` : `${email} does not exist`
				if (msg !== null) error_msg = msg
			new LocalNotification(error_msg, "", "error");
		}

		let { reason, reload } = _.get(location, "state", {}) as any;
		if (reload || shouldReload) {
			let newState = { ...(location.state as any), reload: false };
			history.replace({ ...history.location, ...{ state: newState } });
			setShouldReload(false);
			window.location.reload(); // history.go(0) may not reload page in firebox or safari, thus we specific use location.reload;
		} else if (reason === "timeout") new LocalNotification("Your session is expired.", "", "warning");
	}, [location, history, shouldReload]);

	return (
		<Board className="lg:min-w-8 lg:w-1/5 lg:max-w-screen-xl">
			<div className="py-4 px-7 lg:py-10 lg:relative lg:px-0 lg:w-3/4 lg:mx-auto">
				<Form title="Sign In" data={data} error={error} setError={setError} setData={setData} onSubmit={onSignIn}>
					<FormItem
						type="email"
						name="email"
						label="Work Email"
						placeholder="Charlotte@workemail.com"
						rules={[{ message: "Please input a valid Email address.", validator: (email: string) => isEmail(email) }]}
					/>
					{!isSsoAuth && (
						<FormItem
							type="password"
							name="password"
							label="Password"
							hint="All symbols are allowed. Minimum of 8 characters."
							rules={[
								{
									message: "Password should be at lease 8 characters.",
									validator: (password: string) => password.length >= 8
								}
							]}
						/>
					)}
					{isSsoAuth ? (
						<Button
							label="Sign In"
							className={classNames("w-full text-white mt-10", {
								"bg-blue-100 hover:shadow-hover  shadow-button": validated && !loading,
								"cursor-not-allowed bg-black-5 text-black-20  font-semibold": !validated && !loading,
								"bg-transparent": !!loading
							})}
							disabled={!validated}
							loading={loading}
							type="submit"
						/>
					) : (
						<Button
							label="Sign In"
							className={classNames("w-full text-white mt-10", {
								"bg-blue-100 hover:shadow-hover  shadow-button": validated && !loading,
								"cursor-not-allowed bg-black-5 text-black-20  font-semibold": !validated && !loading,
								"bg-transparent": !!loading
							})}
							disabled={!validated}
							loading={loading}
							type="submit"
						/>
					)}
				</Form>
				
				<div className="w-full flex justify-center">
					<Button label="Reset Password" className="text-black-60 font-semibold mx-auto mt-5" onClick={onResetPWD} />
				</div>
				
				<div>
					<button onClick={ssoSignIn} className="text-14 font-semibold leading-5 h-10 rounded-2 focus:outline-none flex items-center justify-center w-full text-white mt-10 bg-blue-100 hover:shadow-hover  shadow-button">
						<span className="m-2">
							{isSsoAuth ? "Sign in with Password" : "Sign in with SSO"}
						</span>
					</button>
				</div>
				
				<div>
					<button onClick={googleSignIn} className="text-14 font-semibold leading-5 h-10 rounded-2 focus:outline-none flex items-center justify-center w-full text-white mt-2 bg-blue-100 hover:shadow-hover  shadow-button">
						<img className="w-6 h-6 m-2" src="https://lh3.googleusercontent.com/COxitqgJr1sJnIDe8-jiKhxDx1FrYbtRHKJ9z_hELisAlapwE9LUPh6fcXIfb5vwpbMl4xl9H9TRFPc5NOO8Sb3VSgIBrfRYvW6cUA"
							loading="lazy" alt="google logo"/>
						<span className="m-2">Sign in with Google</span>
					</button>
				</div>
			</div>
		</Board>
	);
};

export default memo(withRouter(SignIn));
